import { type ChangeEvent, useRef } from 'react';

import { Icon } from '../Icon';
import { Button } from '../Button';
import styles from './Upload.module.css';

export interface UploadProps {
  accept?: string;
  ariaLabel: string;
  className?: string;
  description?: string;
  disabled?: boolean;
  files?: readonly UploadFile[];
  maxCount?: number;
  maxSize?: number;
  multiple?: boolean;
  onClick?: () => void;
  onExceed?: (files: readonly File[], maxCount: number) => void;
  onPreview?: (id: string) => void;
  onRemove?: (id: string) => void;
  onSelect?: (files: readonly File[]) => void;
  onSizeExceed?: (files: readonly File[], maxSize: number) => void;
  size?: 'default' | 'compact';
  triggerText?: string;
  variant?: 'button' | 'picture-card';
}

export interface UploadFile {
  id: string;
  name: string;
  url?: string;
}

/**
 * 图片与普通文件上传的统一选择入口和受控列表。
 * 组件负责原生文件选择，上传请求与持久化由业务页承接。
 */
export function Upload({ accept, ariaLabel, className, description, disabled = false, files = [], maxCount, maxSize, multiple = false, onClick, onExceed, onPreview, onRemove, onSelect, onSizeExceed, size = 'default', triggerText = '点击上传', variant = 'button' }: UploadProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const atLimit = maxCount !== undefined && files.length >= maxCount;

  const handleTriggerClick = () => {
    onClick?.();
    inputRef.current?.click();
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(event.currentTarget.files ?? []);
    const sizeAcceptedFiles = maxSize === undefined ? selectedFiles : selectedFiles.filter((file) => file.size <= maxSize);
    const oversizedFiles = maxSize === undefined ? [] : selectedFiles.filter((file) => file.size > maxSize);
    const availableCount = maxCount === undefined ? sizeAcceptedFiles.length : Math.max(maxCount - files.length, 0);
    const acceptedFiles = sizeAcceptedFiles.slice(0, availableCount);
    const excessFiles = sizeAcceptedFiles.slice(availableCount);

    if (acceptedFiles.length > 0) onSelect?.(acceptedFiles);
    if (excessFiles.length > 0 && maxCount !== undefined) onExceed?.(excessFiles, maxCount);
    if (oversizedFiles.length > 0 && maxSize !== undefined) onSizeExceed?.(oversizedFiles, maxSize);
    event.currentTarget.value = '';
  };

  const uploadTrigger = variant === 'button'
    ? <Button aria-label={ariaLabel} disabled={disabled || atLimit} leadingIcon="plus" onClick={handleTriggerClick}>{triggerText}</Button>
    : <button aria-label={ariaLabel} className={styles.trigger} disabled={disabled || atLimit} onClick={handleTriggerClick} type="button"><Icon name="plus" size="lg" /></button>;

  const fileList = files.length > 0 ? <div className={styles.fileList}>
    {files.map((file) => <div className={styles.file} data-status="success" key={file.id}>
      {variant === 'picture-card' && file.url
        ? <img alt={file.name} className={styles.preview} src={file.url} />
        : <span className={styles.fileName}>{file.name}</span>}
      {variant === 'picture-card'
        ? <>
          <span className={styles.successMark}><Icon label={`${file.name} 上传成功`} name="check" size="sm" /></span>
          {(onPreview || onRemove) ? <div className={styles.actions}>
            {onPreview ? <button aria-label={`预览 ${file.name}`} className={styles.action} disabled={disabled} onClick={() => onPreview(file.id)} type="button"><Icon name="view" size="md" /></button> : null}
            {onRemove ? <button aria-label={`移除 ${file.name}`} className={styles.action} disabled={disabled} onClick={() => onRemove(file.id)} type="button"><Icon name="delete-filled" size="md" /></button> : null}
          </div> : null}
        </>
        : <>
          <Icon className={styles.listSuccess} label={`${file.name} 上传成功`} name="circle-check" size="sm" />
          {onRemove ? <button aria-label={`移除 ${file.name}`} className={styles.remove} disabled={disabled} onClick={() => onRemove(file.id)} type="button"><Icon name="close" size="xs" /></button> : null}
        </>}
    </div>)}
  </div> : null;

  return <div className={[styles.root, className].filter(Boolean).join(' ')} data-size={size} data-variant={variant}>
    {variant === 'picture-card' ? <div className={styles.pictureGrid}>{fileList}{atLimit ? null : uploadTrigger}</div> : <>{uploadTrigger}{fileList}</>}
    {description ? <span className={styles.description}>{description}</span> : null}
    <input accept={accept} aria-label={`${ariaLabel} 文件选择`} className={styles.fileInput} disabled={disabled || atLimit} multiple={multiple} onChange={handleFileChange} ref={inputRef} type="file" />
  </div>;
}
